import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import LensIcon from '@mui/icons-material/Lens';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid2,
  MenuItem,
  Select,
  Typography,
  Button,
  ButtonGroup,
  useMediaQuery,
  useTheme,
  Popover,
  styled,
} from '@mui/material';
import cx from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { getSetupTopPodcastsChart, findCountryWithTodayData } from '../../utils/chart';
import {
  setSelectedCountry,
  setGenres,
  toggleHiddenGenre,
  setDateRange as setDateRangeFromStore,
} from '../../redux/slices/podcastsSlice';
import PodcastsCarousel from '../PodcastsCarousel/PodcastsCarousel';
import point from '../../assets/images/point.svg';
import TimelineIcon from '@mui/icons-material/Timeline';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import { useStyles, DarkDateRangeStyled } from './styles';
import EpisodeTooltip from '../EpisodeTooltip/EpisodeTooltip';
import PodcastTooltip from '../PodcastTooltip/PodcastTooltip';
import { initialState, options } from './chartConfig';
import LastRank from '../LastRank/LastRank';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { useRemoveUrlParam } from '../../hooks/useRemoveUrlParam';
// Register the necessary components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const DarkDateRange = styled(Box)(DarkDateRangeStyled);

const SEVEN_DAYS = { label: '7d', value: 7 };
const THIRTY_DAYS = { label: '30d', value: 30 };
const SIXTY_DAYS = { label: '60d', value: 60 };
const CUSTOM_DATE_RANGE = { label: 'custom', value: 0 };

const Chart = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [chartData, setChartData] = useState(initialState);
  const [activeRange, setActiveRange] = useState(isMobile ? SEVEN_DAYS : THIRTY_DAYS);
  const [podcastTooltip, setPodcastTooltip] = useState(null);
  const [completeAnimation, setCompleteAnimation] = useState(false);
  const containerRef = useRef(null);
  const {
    currentPodcast,
    topPodcasts: { data, loading },
    selectedCountry: chosenCountry,
    selectedGenre,
    hiddenGenres,
    demoModePodcasts,
    episodes,
  } = useSelector((state) => state.podcasts);
  const chart = useRef(null);
  const [episodeTooltip, setEpisodeTooltip] = useState();
  const [lastRankTooltip, setLastRankTooltip] = useState();
  const [dateRange, setDateRange] = useState([
    {
      startDate: moment().toDate(),
      endDate: moment().toDate(),
      key: 'selection',
    },
  ]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [countries, setCountries] = useState([]);
  const { isLight } = useSelector((state) => state.theme);
  const [currentGenres, setCurrentGenres] = useState([]);
  const [searchParams] = useSearchParams();
  const { removeParam } = useRemoveUrlParam();
  
  const handlePodcastTooltip = (ctx) => {
    if (!ctx.raw.podcast) return;

    const tooltipWidth = 127; // Width of the tooltip from styles
    const screenWidth = window.innerWidth;
    const xPosition = ctx.element.x;

    // Check if tooltip would overflow the right side
    const isMoveLeft = screenWidth - xPosition < tooltipWidth;

    setPodcastTooltip({
      podcast: { ...ctx.raw.podcast, images: currentPodcast.artworkUrl60 },
      element: ctx.element,
      isMoveLeft,
    });
  };

  const drawPoint = (ctx) => {
    if (!ctx?.raw) return;
    let image = null;
    if (ctx.raw.isLastRank) {
      setLastRankTooltip({ lastRank: ctx.raw.y, element: ctx.element });
    }
    if (ctx.raw.episode) {
      image = new Image();
      image.src = point;
      image.width = '20';
      image.height = '20';
    }
    if (ctx.active && ctx.raw.episode) {
      setEpisodeTooltip({ episode: ctx.raw.episode, element: ctx.element });
    }

    if (ctx.active && ctx.raw.podcast) {
      handlePodcastTooltip(ctx);
    }

    return image;
  };

  // useEffect(() => {
  //   if (!data?.length) return;
  //   const countries = [...new Set(data.map((item) => item.country))].map((country) =>
  //     country === 'UK' ? 'United Kingdom' : country
  //   );
  //   const sortedCountries = countries.includes('United States')
  //     ? ['United States', ...countries.filter((country) => country !== 'United States')]
  //     : countries;
  //   setCountries(sortedCountries);
  //   const country = searchParams.get('country');
  //   if (country) {
  //     dispatch(setSelectedCountry(country));
  //     removeParam('country');
  //   } else {
  //     dispatch(setSelectedCountry(sortedCountries[0]));
  //   }
  // }, [currentPodcast]);

  useEffect(() => {
    if (!data?.length) return;
    const countries = [...new Set(data.map((item) => item.country))].map((country) =>
      country === 'UK' ? 'United Kingdom' : country
    );
    const sortedCountries = countries.includes('United States')
      ? ['United States', ...countries.filter((country) => country !== 'United States')]
      : countries;
    setCountries(sortedCountries);
    if (sortedCountries.length) {
      const isDemoMode = demoModePodcasts.length > 0;
      const countryExists = sortedCountries.includes(chosenCountry);

      const country = searchParams.get('country');
      if (country) {
        dispatch(setSelectedCountry(country));
        removeParam('country');
      } else if (!countryExists || isDemoMode || sortedCountries[0] !== 'United States') {
        const countryWithFreshData = findCountryWithTodayData(data, sortedCountries, isDemoMode);
        dispatch(setSelectedCountry(countryWithFreshData));
      }
    }
  }, [data, demoModePodcasts, currentPodcast, dispatch]);

  useEffect(() => {
    setCompleteAnimation(false);
    setLastRankTooltip(null);
    setEpisodeTooltip(null);
    setPodcastTooltip(null);
    if (!data?.length) return;

    try {
      const filteredData = hiddenGenres.length
        ? data.map((item) => ({ ...item, hidden: hiddenGenres.includes(item.genre) }))
        : data;

      // Prepare custom date range if active
      const customDateRange = activeRange.label === CUSTOM_DATE_RANGE.label ? {
        startDate: dateRange[0].startDate,
        endDate: dateRange[0].endDate
      } : null;
      
      let setup = getSetupTopPodcastsChart(
        filteredData,
        chosenCountry,
        activeRange.value,
        episodes,
        isMobile,
        customDateRange
      );

      // const isDemoMode = demoModePodcasts.length > 0;
      // if (setup.datasets?.every((dataset) => !dataset.data?.length) && !isDemoMode) {
      //   setActiveRange(SIXTY_DAYS);
      //   setup = getSetupTopPodcastsChart(filteredData, chosenCountry, SIXTY_DAYS.value, episodes, isMobile);
      // }

      const numData = setup.labels.length;

      const maxY =
        setup.datasets
          ?.filter((dataset) => !dataset.hidden)
          .reduce((max, { data = [] }) => Math.max(max, ...data.map((point) => point.y || 1)), 1) || 1;
      // Find minimum Y value from visible datasets
      const minY =
        setup.datasets
          ?.filter((dataset) => !dataset.hidden)
          .reduce((min, { data = [] }) => Math.min(min, ...data.map((point) => point.y || 1)), Infinity) || 1;
      // Configure chart options if chart reference exists
      if (chart.current) {
        chart.current.data.labels = [];
        chart.current.data.datasets.forEach((dataset) => {
          dataset.data = [];
        });
        // Set y-axis max based on maxY ranges
        switch (true) {
          case maxY <= 50:
            chart.current.options.scales.y.max = 50;
            break;
          case maxY <= 100:
            chart.current.options.scales.y.max = 100;
            break;
          case maxY <= 150:
            chart.current.options.scales.y.max = 150;
            break;
          default:
            chart.current.options.scales.y.max = 200;
        }

        // Set y-axis min based on minY
        switch (true) {
          case minY <= 50:
            chart.current.options.scales.y.min = 1;
            break;
          case minY <= 100:
            chart.current.options.scales.y.min = 50;
            break;
          case minY <= 150:
            chart.current.options.scales.y.min = 100;
            break;
          case minY <= 200:
            chart.current.options.scales.y.min = 150;
            break;
          default:
            chart.current.options.scales.y.min = 1;
        }

        // Configure layout based on device and data size
        if (isMobile) {
          chart.current.options.scales.y.position = { x: 0 };
          chart.current.options.layout.padding = { left: 20, right: 20 };
          chart.current.options.maintainAspectRatio = false;
        } else if (numData <= 10) {
          chart.current.options.scales.y.position = { x: 0 };
          chart.current.options.layout.padding = { left: 150, right: 150 };
        } else {
          chart.current.options.scales.y.position = { x: Math.ceil(numData / 10) - numData / 100 };
          chart.current.options.layout.padding = { left: 0, right: 0 };
        }
        // Set animation and interaction handlers
        chart.current.options.animation = {
          onComplete: () => setCompleteAnimation(true),
        };
        chart.current.options.elements.point.pointStyle = drawPoint;
        chart.current.options.onHover = (event, elements) => {
          if (!event) return;
          if (!elements || !elements.length) {
            setPodcastTooltip(null);
            setEpisodeTooltip(null);
          }
        };
        chart.current.options.scales.y.grid.color = isLight ? '#E0E0E0' : '#292929';

        const labels = setup.labels || [];

        // Find the first and last labels that don't contain ": f"
        let isfirstRealLabel = false;
        let firstRealLabel = null;
        let lastRealLabelIndex = labels.map((label, index) => ({ label, index }))
          .filter(item => !item.label.includes(': f'))
          .pop()?.index;
    
        // Configure x-axis grid with custom color for first and last labels without ": f"
        chart.current.options.scales.x.grid.color = (context) => {
          const tickValue = context.tick?.label;
          if (tickValue && !isfirstRealLabel || tickValue === firstRealLabel) {
            firstRealLabel = tickValue;
            isfirstRealLabel = true;
            return isLight ? '#a8a8a8' : '#585858'; // Custom color for the first and last real data lines
          }
          if (context.index === lastRealLabelIndex && lastRealLabelIndex !== undefined) {
            return isLight ? '#a8a8a8' : '#585858'; // Custom color for the first and last real data lines
          }
          
          return isLight ? '#E0E0E0' : '#292929'; // Default color for other lines
        };
        chart.current.update();
      }

      dispatch(setGenres(setup.genres));
      setCurrentGenres(setup.genres);
      setChartData(setup);
    } catch (error) {
      console.log(error);
    }
  }, [data, chosenCountry, hiddenGenres, activeRange, isMobile, isLight, demoModePodcasts]);

  useEffect(() => {
    if (!containerRef.current || !chart.current) return;

    const handleResize = () => {
      if (chart.current && containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const newAspectRatio = isMobile ? 2 : width < 768 ? 3 : 5;
        chart.current.options.aspectRatio = newAspectRatio;
        chart.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    // Initial resize
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  const handleChangeCountry = (event) => {
    const country = event.target.value;
    dispatch(setSelectedCountry(country));
  };

  const handleHideGenre = (name) => {
    dispatch(toggleHiddenGenre(name));
  };

  const handleDateRangeChange = (range) => {
    if (activeRange.label === CUSTOM_DATE_RANGE.label) {
      dispatch(
        setDateRangeFromStore({
          startDate: moment().subtract(60, 'days').toDate(),
          endDate: moment().toDate(),
        })
      );
    }
    setActiveRange(range);
    setDateRange([{ startDate: moment().toDate(), endDate: moment().toDate(), key: 'selection' }]);
  };

  const handleCalendarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setAnchorEl(null);
  };

  const handleDateRangeSelect = (ranges) => {
    setDateRange([ranges.selection]);
  };

  const handleApplyDateRange = () => {
    const { startDate, endDate } = dateRange[0];
    const start = moment(startDate);
    const end = moment(endDate);
    const days = end.diff(start, 'days');
    dispatch(
      setDateRangeFromStore({
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
      })
    );
    setActiveRange({ ...CUSTOM_DATE_RANGE, value: days || 1 });

    handleCalendarClose();
  };

  return (
    <Box
      className={cx(classes.chartWrapper, {
        [classes.chartWrapperNoGenre]: !selectedGenre,
      })}
    >
      {selectedGenre && (
        <Box className={classes.carouselBackdrop}>
          <PodcastsCarousel />
        </Box>
      )}
      <Box
        ref={containerRef}
        className={cx(classes.chartContent, {
          [classes.blurred]: selectedGenre,
        })}
      >
        <Grid2
          className={classes.header}
          container
          item
          md={12}
          spacing={1}
          rowSpacing={2}
          justifyContent="space-between"
        >
          <Grid2
            container
            item
            md={3}
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{ flexWrap: "nowrap" }}
          >
            <Grid2 item>
              <FormControl variant="outlined">
                <Select
                  defaultValue={chosenCountry}
                  value={chosenCountry}
                  onChange={handleChangeCountry}
                  className={classes.select}
                  disabled={loading || !currentPodcast}
                >
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>
            <Grid2 item>
              <ButtonGroup variant="outlined" size="small">
                <Button
                  onClick={() => handleDateRangeChange(SEVEN_DAYS)}
                  variant={
                    activeRange.label === SEVEN_DAYS.label
                      ? "contained"
                      : "outlined"
                  }
                >
                  7d
                </Button>
                <Button
                  onClick={() => handleDateRangeChange(THIRTY_DAYS)}
                  variant={
                    activeRange.label === THIRTY_DAYS.label
                      ? "contained"
                      : "outlined"
                  }
                >
                  30d
                </Button>
                <Button
                  onClick={() => handleDateRangeChange(SIXTY_DAYS)}
                  variant={
                    activeRange.label === SIXTY_DAYS.label
                      ? "contained"
                      : "outlined"
                  }
                >
                  60d
                </Button>
                <Button
                  variant={
                    activeRange.label === CUSTOM_DATE_RANGE.label
                      ? "contained"
                      : "outlined"
                  }
                  onClick={handleCalendarClick}
                >
                  <CalendarTodayIcon fontSize="small" />
                </Button>
              </ButtonGroup>
            </Grid2>
          </Grid2>
          <Grid2 container item md={9}>
            {currentGenres.map(({ name, color }, i) => (
              <Grid2
                container
                item
                md={3}
                key={name}
                sx={{ cursor: "pointer" }}
                onClick={() => handleHideGenre(name)}
              >
                <Box>
                  <LensIcon sx={{ color }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      color: "var(--text-color)",
                      textDecoration: hiddenGenres.includes(name)
                        ? "line-through"
                        : "none",
                    }}
                  >
                    {name}
                  </Typography>
                </Box>
              </Grid2>
            ))}
          </Grid2>
        </Grid2>
        <Grid2 className={classes.chart} container item md={12}>
          {completeAnimation && episodeTooltip && (
            <EpisodeTooltip
              key={episodeTooltip.episode.title}
              element={episodeTooltip.element}
              num={episodeTooltip.episode.numEpisode}
              title={episodeTooltip.episode.title}
            />
          )}
          {podcastTooltip && (
            <PodcastTooltip
              element={podcastTooltip?.element}
              podcast={podcastTooltip?.podcast}
              isMoveLeft={podcastTooltip?.isMoveLeft}
            />
          )}
          {completeAnimation && lastRankTooltip && (
            <LastRank
              num={lastRankTooltip.lastRank}
              element={lastRankTooltip.element}
            />
          )}
          {!isMobile && (
            <div
              className={cx(classes.blur, classes.startBlur)}
              style={{ height: chart.current?.height }}
            />
          )}

          {loading || (!currentPodcast && !demoModePodcasts.length) ? (
            <Box display="flex" justifyContent="center" width="100%">
              <CircularProgress />
            </Box>
          ) : data?.length ? (
            <Line ref={chart} data={chartData} options={options} />
          ) : (
            currentPodcast && (
              <Box
                display="flex"
                justifyContent="center"
                width="100%"
                className={classes.noData}
              >
                <TimelineIcon />
                <Typography>
                  We are only able to display data for the top 200 in Apple
                  Charts or top 100 in Spotify Charts. This podcast has not been
                  on these charts in this time period.
                </Typography>
              </Box>
            )
          )}
          {!isMobile && (
            <div
              className={cx(classes.blur, classes.endBlur)}
              style={{ height: chart.current?.height }}
            />
          )}
        </Grid2>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCalendarClose}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "#1c1b1b",
            color: theme.palette.text.primary,
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <DarkDateRange>
          <DateRange
            ranges={dateRange}
            maxDate={moment().toDate()}
            minDate={moment("2024-10-22").toDate()}
            onChange={handleDateRangeSelect}
            scroll={{ enabled: true }}
            showSelectionPreview={true}
            rangeColors={[theme.palette.primary.main]}
          />
        </DarkDateRange>
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            backgroundColor: "var(--background-color)",
          }}
        >
          <Button
            variant="contained"
            onClick={handleApplyDateRange}
            color="primary"
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default Chart;
